import { useEffect, useState } from "react";

interface ShareModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
}

export default function ShareModal({ open, title, onClose }: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (open) {
      setUrl(window.location.href);
      setCopied(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const helper = document.createElement("textarea");
      helper.value = url;
      helper.setAttribute("readonly", "true");
      helper.style.position = "fixed";
      helper.style.opacity = "0";
      document.body.appendChild(helper);
      helper.select();
      document.execCommand("copy");
      document.body.removeChild(helper);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2600);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
      onClick={onClose}
    >
      <div
        className="anim-fade-up w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="share-modal-title" className="text-lg font-bold text-foreground-950">分享这条黑榜视频</h2>
            <p className="mt-1 text-xs text-foreground-500">复制链接后发送给同样关注赛事的朋友</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭分享弹窗"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="mt-4 rounded-md border border-background-200 bg-background-100 px-3 py-3">
          <p className="line-clamp-2-custom text-[13px] font-medium text-foreground-800">{title}</p>
          <div className="mt-3 flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={url}
              aria-label="视频分享链接"
              className="h-9 min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-2.5 text-xs text-foreground-700 outline-none"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="btn-primary h-9 shrink-0 px-3 text-xs"
            >
              {copied ? "已复制" : "复制链接"}
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
          <i className={copied ? "ri-checkbox-circle-fill text-base text-primary-600" : "ri-links-line text-base"} />
          {copied ? "链接已复制到剪贴板，可以直接粘贴分享。" : "站内内容为演示数据，分享链接仅在本站内有效。"}
        </div>
      </div>
    </div>
  );
}